import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LEAD_SECTIONS, EMPTY_LEAD, REQUIRED_FIELD_NAMES } from "@/lib/leadSchema";
import QuickExclusionPanel from "@/components/QuickExclusionPanel";
import { CampaignReferencePanel } from "@/components/CampaignReferencePanel";
import PhoneInputField, { countryNameToIso2 } from "@/components/PhoneInputField";
import { getCountryCallingCode, parsePhoneNumber } from "libphonenumber-js";
import { ArrowLeft, Trash2, CheckCircle2, AlertTriangle, Loader2, ChevronDown, Check } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const QA_EDITABLE = new Set([
  "qa_audit_status", "qa_name", "tc_name", "lg_comments", "qa_comments", "data_comments",
  // QA + Team Lead are explicitly allowed to backdate / override the Date
  // field — agents are NOT (enforced both in UI + on the backend).
  "date",
]);

// Roles permitted to override the auto-stamped "today" on the Date field.
// Agents (and any other role not in this set) see a locked Date input.
const DATE_OVERRIDE_ROLES = new Set(["admin", "team_lead", "qa"]);

// Returns yyyy-mm-dd for "today" in the user's local time zone — matches
// the format <input type="date"> emits.
const todayIsoDate = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const SBT_LOGO = "https://customer-assets.emergentagent.com/job_lead-exclusion-check/artifacts/lcmlqwgq_SBT%20logo.jpg";
const MARTECH_LOGO = "https://customer-assets.emergentagent.com/job_lead-exclusion-check/artifacts/12jtzz2h_martech%20logo.png";

/**
 * SurveyQuestionField — renders one inline survey question from the
 * picked campaign on the Add Lead form. Read-only on Edit per admin spec.
 */
function SurveyQuestionField({ question, value, onChange, required, readOnly }) {
  const id = `survey-q-${question.id}`;
  const dataTestId = `survey-q-${question.id}`;
  const t = question.type;
  const baseInput = "h-9 rounded-sm font-body w-full text-sm border border-input bg-background px-3";
  const disabled = readOnly;

  let control = null;
  if (t === "short_text") {
    control = (
      <input id={id} data-testid={dataTestId} value={value || ""}
             disabled={disabled}
             onChange={(e) => onChange(e.target.value)}
             className={baseInput} />
    );
  } else if (t === "long_text") {
    control = (
      <textarea id={id} data-testid={dataTestId} value={value || ""}
                disabled={disabled} rows={3}
                onChange={(e) => onChange(e.target.value)}
                className="rounded-sm font-body w-full text-sm border border-input bg-background px-3 py-2" />
    );
  } else if (t === "single_choice" || t === "yes_no") {
    const opts = t === "yes_no" ? ["Yes", "No"] : (question.options || []);
    control = (
      <select id={id} data-testid={dataTestId} value={value || ""}
              disabled={disabled}
              onChange={(e) => onChange(e.target.value)}
              className={baseInput}>
        <option value="">— Select —</option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  } else if (t === "multi_choice") {
    const selected = Array.isArray(value) ? value : [];
    const opts = question.options || [];
    const toggle = (opt) => {
      if (disabled) return;
      const next = selected.includes(opt)
        ? selected.filter((x) => x !== opt)
        : [...selected, opt];
      onChange(next);
    };
    const summary = selected.length === 0
      ? "— Select —"
      : selected.length <= 2
      ? selected.join(", ")
      : `${selected.slice(0, 2).join(", ")} +${selected.length - 2} more`;
    control = (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            data-testid={dataTestId}
            disabled={disabled}
            className={`${baseInput} flex items-center justify-between gap-2 text-left ${
              selected.length === 0 ? "text-muted-foreground" : ""
            } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-muted/40"}`}
          >
            <span className="truncate">{summary}</span>
            <ChevronDown size={14} className="shrink-0 opacity-60" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[--radix-popover-trigger-width] p-1"
          data-testid={`${dataTestId}-menu`}
        >
          <div className="max-h-60 overflow-y-auto">
            {opts.length === 0 ? (
              <div className="px-3 py-2 text-xs text-muted-foreground">No options</div>
            ) : (
              opts.map((o) => {
                const on = selected.includes(o);
                return (
                  <button
                    type="button"
                    key={o}
                    onClick={() => toggle(o)}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs text-left transition-colors ${
                      on ? "bg-primary/10 text-foreground" : "hover:bg-muted"
                    }`}
                    data-testid={`${dataTestId}-opt-${o}`}
                  >
                    <span
                      className={`flex h-4 w-4 items-center justify-center rounded-sm border ${
                        on ? "bg-primary border-primary text-primary-foreground" : "border-input bg-background"
                      }`}
                    >
                      {on && <Check size={11} strokeWidth={3} />}
                    </span>
                    <span className="flex-1 truncate">{o}</span>
                  </button>
                );
              })
            )}
          </div>
          {selected.length > 0 && !disabled && (
            <div className="border-t border-border mt-1 pt-1 flex justify-end">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground px-2 py-1"
                data-testid={`${dataTestId}-clear`}
              >
                Clear
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    );
  } else if (t === "rating") {
    const minV = question.min_value ?? 1;
    const maxV = question.max_value ?? 5;
    const opts = [];
    for (let i = minV; i <= maxV; i++) opts.push(i);
    control = (
      <select id={id} data-testid={dataTestId} value={value ?? ""}
              disabled={disabled}
              onChange={(e) => onChange(e.target.value ? Number(e.target.value) : "")}
              className={baseInput}>
        <option value="">— Select —</option>
        {opts.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
    );
  } else if (t === "number") {
    control = (
      <input type="number" id={id} data-testid={dataTestId}
             value={value ?? ""} disabled={disabled}
             onChange={(e) => onChange(e.target.value ? Number(e.target.value) : "")}
             className={baseInput} />
    );
  } else if (t === "date") {
    control = (
      <input type="date" id={id} data-testid={dataTestId}
             value={value || ""} disabled={disabled}
             onChange={(e) => onChange(e.target.value)}
             className={baseInput} />
    );
  } else {
    control = (
      <input id={id} data-testid={dataTestId} value={value || ""}
             disabled={disabled}
             onChange={(e) => onChange(e.target.value)}
             className={baseInput} />
    );
  }

  return (
    <div data-testid={`${dataTestId}-wrap`}>
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id} className="text-xs uppercase tracking-wider text-muted-foreground">
          {question.prompt}
          {required && !readOnly && <span className="text-red-600 ml-1">*</span>}
          {readOnly && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-sm badge-info">read-only</span>}
        </Label>
        {question.survey_title && (
          <span className="text-[10px] text-muted-foreground">{question.survey_title}</span>
        )}
      </div>
      <div className="mt-1">{control}</div>
    </div>
  );
}



function FieldControl({ field, value, onChange, optionMap, disabled, onBlur, countryName }) {
  const id = `field-${field.name}`;
  const dataTestId = `field-${field.name}`;
  const baseInput = "h-9 rounded-sm font-body";

  if (field.type === "phone") {
    return (
      <PhoneInputField
        value={value || ""}
        onChange={(v) => onChange(field.name, v)}
        countryName={countryName}
        disabled={disabled}
        testid={dataTestId}
      />
    );
  }
  if (field.type === "textarea") {
    return (
      <Textarea
        id={id} data-testid={dataTestId}
        value={value || ""} disabled={disabled}
        onChange={(e) => onChange(field.name, e.target.value)}
        rows={3} className="rounded-sm"
      />
    );
  }
  if (field.type === "select" || field.type === "select-dynamic") {
    const src = field.source;
    let options = [];
    if (src === "campaigns") options = optionMap.campaigns;
    else if (src === "agents") options = optionMap.agents;
    else if (src === "countries") options = optionMap.countries;
    else if (src === "departments") options = optionMap.departments;
    else if (src === "employee_sizes") options = optionMap.employee_sizes;
    else if (src === "revenue_sizes") options = optionMap.revenue_sizes;
    else if (src === "linkedin_industries") options = optionMap.linkedin_industries;
    else if (src === "email_asset_statuses") options = optionMap.email_asset_statuses;
    else if (src === "qa_audit_statuses") options = optionMap.qa_audit_statuses;
    else if (src === "campaign_types") options = optionMap.campaign_types;
    else if (src === "campaign_ids") options = optionMap.campaign_ids;
    else options = field.options || [];
    return (
      <select
        id={id} data-testid={dataTestId}
        value={value || ""} disabled={disabled}
        onChange={(e) => onChange(field.name, e.target.value)}
        className={`w-full px-3 border border-input bg-background text-sm ${baseInput}`}
      >
        <option value="">— Select —</option>
        {(options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return (
    <Input
      id={id} data-testid={dataTestId}
      type={field.type === "email" ? "email" : field.type === "date" ? "date" : "text"}
      value={value || ""} disabled={disabled}
      onChange={(e) => onChange(field.name, e.target.value)}
      onBlur={onBlur}
      placeholder={field.name === "website" ? "e.g. www.xyz.com" : undefined}
      className={baseInput}
    />
  );
}

function EmailValidationBadge({ state }) {
  if (!state) return null;
  if (state.loading) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid="email-validating">
        <Loader2 size={12} className="animate-spin" /> Validating…
      </span>
    );
  }
  const aiSuffix = state.ai_score != null ? ` · AI ${state.ai_score}/10` : "";
  // Catch-all accepted is real but lower-confidence — render as yellow, not green.
  if (state.acceptable && state.status === "catch-all") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-sm" data-testid="email-validation-catchall">
        <AlertTriangle size={12} /> catch-all{aiSuffix} · accepted
      </span>
    );
  }
  if (state.acceptable) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 badge-success px-2 py-0.5 rounded-sm" data-testid="email-validation-pass">
        <CheckCircle2 size={12} /> {state.status}{state.sub_status ? ` · ${state.sub_status}` : ""}{aiSuffix}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs badge-error px-2 py-0.5 rounded-sm" data-testid="email-validation-fail">
      <AlertTriangle size={12} /> {state.status}{state.sub_status ? ` · ${state.sub_status}` : ""}{aiSuffix}
    </span>
  );
}

// CampaignReferencePanel now lives in /components/CampaignReferencePanel.jsx
// to be shared between LeadForm and CampaignExclusion.

export default function LeadForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { options, user } = useAuth();
  const role = user?.role || "agent";
  const isQA = role === "qa";
  // True base admin only. HR/TL custom roles have base=admin (which gets
  // rewritten onto `user.role`); we use the un-rewritten `role_label`
  // so HR/TL don't unlock admin-only fields in the lead form.
  const isAdmin = (user?.role_label || role) === "admin";

  const [form, setForm] = useState(() => ({ ...EMPTY_LEAD, date: todayIsoDate() }));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [emailState, setEmailState] = useState(null); // {loading,status,sub_status,acceptable,email}
  const [duplicateState, setDuplicateState] = useState(null); // {checking,duplicate,match_count,first_match,email}
  const [campaignRef, setCampaignRef] = useState(null);
  const [fieldPerms, setFieldPerms] = useState(null); // { <field_name>: "edit"|"view"|"hidden" } | null=allow all
  const emailDebounceRef = useRef(null);
  const formTopRef = useRef(null);

  // Fetch effective field permissions for the current user
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/permissions/effective");
        if (!cancelled) setFieldPerms(data.permissions || {});
      } catch {
        if (!cancelled) setFieldPerms({}); // fall back to default-allow
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Compute effective per-render perms: assignment override grants admin-equivalent
  // full-edit access on every field, regardless of the user's role permissions.
  const isAssignee = useMemo(() => {
    const email = (user?.email || "").trim().toLowerCase();
    if (!email) return false;
    return [
      form.assigned_agent, form.assigned_team_lead,
      form.assigned_auditor, form.assigned_email_marketing,
    ].some((v) => (v || "").trim().toLowerCase() === email);
  }, [user, form.assigned_agent, form.assigned_team_lead, form.assigned_auditor, form.assigned_email_marketing]);
  const effectivePerms = isAssignee ? null : fieldPerms; // null === all-edit

  // Reassigned Agent Name (admin spec 2026-02-25, refined 2026-07-09):
  // On Edit, show a read-only field below Agent Name whenever the
  // lead has been resubmitted by someone other than the creator, OR
  // the current assigned_agent differs from the original creator.
  // Prefer the pre-stamped `resubmitted_by_name`; else use the backend-
  // enriched `assigned_agent_name`; only fall back to the email if the
  // user lookup failed (should be rare — logs a warn in that case).
  const reassignedAgentDisplay = useMemo(() => {
    if (!isEdit) return "";
    const stamped = (form.resubmitted_by_name || "").trim();
    if (stamped) return stamped;
    const assigned = (form.assigned_agent || "").toLowerCase().trim();
    const creator = (form.created_by || "").toLowerCase().trim();
    if (assigned && creator && assigned !== creator) {
      return (form.assigned_agent_name || "").trim() || form.assigned_agent;
    }
    return "";
  }, [isEdit, form.resubmitted_by_name, form.assigned_agent, form.assigned_agent_name, form.created_by]);

  // Whether the current user (assignee, non-creator) can FILL the
  // inline survey questions on Edit. Survey answers stay read-only
  // for everyone else (matches the existing admin spec). Only the
  // FIRST resubmission gets to write — once `resubmitted_by_id` is
  // stamped, the field locks again.
  const canFillSurveyOnEdit = useMemo(() => {
    if (!isEdit) return false;
    if (form.resubmitted_by_id) return false;
    const meId = (user?.id || "").trim();
    const creatorId = (form.created_by_id || "").trim();
    if (!meId || !creatorId) return false;
    if (meId === creatorId) return false;
    return isAssignee;
  }, [isEdit, form.resubmitted_by_id, form.created_by_id, user?.id, isAssignee]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/leads/${id}`);
        setForm({ ...EMPTY_LEAD, ...data });
      } catch {
        toast.error("Failed to load lead");
        navigate(role === "agent" ? "/my-leads" : "/leads");
      }
    })();
  }, [id, isEdit, navigate]);

  // Auto-populate the Lead ID field on NEW leads by fetching the next
  // sequential number from the backend. Only fires when creating (not
  // editing), the field is currently empty, and the user hasn't already
  // typed something. Backend re-checks + reassigns if there's a race.
  useEffect(() => {
    if (isEdit) return;
    if ((form.lead_id || "").trim()) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/leads/next-id");
        if (!cancelled && data?.next) {
          setForm((f) => ((f.lead_id || "").trim() ? f : { ...f, lead_id: data.next }));
        }
      } catch (e) {
        console.debug("[LeadForm] /leads/next-id fetch skipped:", e?.response?.status);
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, form.lead_id]);

  // Load campaign parameters when campaign_name changes
  useEffect(() => {
    const name = form.campaign_name;
    if (!name) { setCampaignRef(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/campaigns/by-name/${encodeURIComponent(name)}`);
        if (!cancelled) {
          setCampaignRef(data);
          // Auto-fill campaign_type from the campaign's Productivity Default
          // when the user hasn't manually set one for this row yet. Users
          // can still override — we only fill an empty slot to avoid
          // stomping on their explicit choice.
          if (data?.campaign_type && !form.campaign_type) {
            setForm((f) => ({ ...f, campaign_type: data.campaign_type }));
          }
        }
      } catch (e) {
        console.warn(
          "[LeadForm] /campaigns/by-name failed for", JSON.stringify(name),
          "→ status", e?.response?.status, "detail", e?.response?.data,
        );
        if (!cancelled) setCampaignRef(null);
      }
    })();
    return () => { cancelled = true; };
  }, [form.campaign_name]);

  // Inline survey questions for the picked campaign. We fetch from
  // /api/campaigns/{id}/lead-form-questions which aggregates every
  // PUBLISHED survey's questions for that campaign in admin-defined
  // order. Rendered below the Location → Country field.
  const [inlineQuestions, setInlineQuestions] = useState([]);
  useEffect(() => {
    const cid = campaignRef?.id;
    if (!cid) { setInlineQuestions([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/campaigns/${cid}/lead-form-questions`);
        if (!cancelled) setInlineQuestions(data.questions || []);
      } catch (e) {
        console.warn("[LeadForm] lead-form-questions failed", e?.response?.status);
        if (!cancelled) setInlineQuestions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [campaignRef?.id]);

  // Admin spec 2026-02-25: when campaign_type ∈ {LG, DG, AG, Sales}
  // all inline questions become mandatory regardless of the
  // survey-level `required` flag.
  const MANDATORY_CT = ["LG", "DG", "AG", "Sales"];
  const allQuestionsRequired = MANDATORY_CT.includes((form.campaign_type || "").trim());

  // When the Location → Country field changes, sync the work_phone's
  // country dialing code:
  //   • If work_phone is empty → seed with just the calling code
  //     (e.g. "+1" for USA) so the dropdown lands on that country.
  //   • If work_phone already has a parseable number whose country
  //     matches → no-op (don't churn the field).
  //   • If work_phone has digits under a different country → swap the
  //     prefix to the new calling code BUT preserve the local digits
  //     the agent already typed (per user spec: "don't wipe their
  //     typed number").
  useEffect(() => {
    const iso = countryNameToIso2(form.country);
    if (!iso) return;
    let newCC;
    try { newCC = getCountryCallingCode(iso); } catch { return; }
    const newPrefix = `+${newCC}`;
    setForm((prev) => {
      const current = prev.work_phone || "";
      if (!current) return { ...prev, work_phone: newPrefix };
      // If the existing number already belongs to the new country,
      // don't churn the field.
      let prevCC = null;
      try {
        const parsed = parsePhoneNumber(current);
        if (parsed?.country === iso) return prev;
        if (parsed?.countryCallingCode) prevCC = String(parsed.countryCallingCode);
      } catch { /* unparseable — keep prevCC=null and use generic strip */ }
      // Strip ONLY the previous calling-code prefix (not all digits!) and
      // prepend the new one — keeps the local digits the agent typed.
      let local;
      if (prevCC) {
        local = current.replace(new RegExp(`^\\+${prevCC}`), "");
      } else {
        // Fallback: assume 1-3 digit calling code per E.164.
        local = current.replace(/^\+\d{1,3}/, "");
      }
      return { ...prev, work_phone: newPrefix + local };
    });
  }, [form.country]);

  // Strict email format check used both for inline error display and to
  // block submit. Matches the same regex validateEmailNow uses for the
  // ZeroBounce pre-flight (no whitespace anywhere + must contain `@x.y`).
  const isEmailFormatValid = (v) => /^\S+@\S+\.\S+$/.test((v || "").trim());

  // Strict website format check — must follow the canonical
  // `www.<domain>.<tld>` shape per admin spec. No protocol (http://),
  // no path, no spaces. Multi-segment TLDs like .co.uk are allowed.
  // Labels accept alphanumerics + hyphens (not leading/trailing).
  const WEBSITE_FORMAT_RE =
    /^www\.([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/;
  const isWebsiteFormatValid = (v) => WEBSITE_FORMAT_RE.test((v || "").trim());

  // ── Trusted-upload bypass (client-side) ──────────────────────────
  // A lead is treated as a "trusted upload" whenever:
  //   1. The server marked it so at bulk-upload time (skip_zerobounce=1)
  //      via the `trusted_upload=true` flag on the lead doc, OR
  //   2. This is Edit mode and the LOADED work_email already fails the
  //      strict format regex — a grandfathering rule so agents can still
  //      resubmit legacy pre-flag trusted uploads without hitting the
  //      "Invalid email format" wall.
  // When trusted, we skip the strict format guards on both email and
  // website (inline errors + submit gate + ZeroBounce pre-flight) so
  // the assignee's Save always lands. Backend mirrors this bypass.
  const isTrustedLead = isEdit && (
    form.trusted_upload === true ||
    (!!form.work_email && !isEmailFormatValid(form.work_email))
  );

  const onChange = (k, v) => {
    // Auto-strip any whitespace from emails as the user types/pastes. This
    // catches the most common copy-paste mistake (e.g. trailing space from
    // Excel cells) without surprising the user — they SEE the bad char
    // get removed.
    if (k === "work_email" && typeof v === "string") {
      v = v.replace(/\s/g, "");
    }
    // Same hygiene for the website field — strip whitespace + lowercase.
    if (k === "website" && typeof v === "string") {
      v = v.replace(/\s/g, "").toLowerCase();
    }
    setForm((f) => ({ ...f, [k]: v }));
    if (k === "work_email") {
      setEmailState(null);
    }
  };

  const validateEmailNow = async (email) => {
    const cleaned = (email || "").trim().toLowerCase();
    if (!cleaned || !/^\S+@\S+\.\S+$/.test(cleaned)) {
      setEmailState(null);
      setDuplicateState(null);
      return;
    }
    setEmailState({ loading: true, email: cleaned });
    setDuplicateState({ checking: true, email: cleaned });
    try {
      const [valRes, dupRes] = await Promise.allSettled([
        api.post("/email/validate", { email: cleaned }),
        // Skip duplicate check on /leads/:id when editing the same record (email unchanged)
        api.post("/exclusion/master", { type: "email", value: cleaned }),
      ]);
      if (valRes.status === "fulfilled") {
        const data = valRes.value.data;
        setEmailState({
          loading: false, email: cleaned,
          status: data.status, sub_status: data.sub_status,
          acceptable: !!data.acceptable,
          ai_score: data.ai_score ?? null,
          catchall_domain: !!data.catchall_domain,
        });
      } else {
        const e = valRes.reason;
        setEmailState({ loading: false, email: cleaned, status: "error",
          sub_status: formatApiErrorDetail(e?.response?.data?.detail) || "request_failed",
          acceptable: false });
      }
      if (dupRes.status === "fulfilled") {
        const data = dupRes.value.data;
        // When editing, if the duplicate is the current record itself, ignore it.
        const isSelf = isEdit && data.matches?.length === 1 && data.matches[0].id === id;
        const duplicate = data.excluded && !isSelf;
        setDuplicateState({
          checking: false, email: cleaned,
          duplicate, match_count: data.match_count,
          first_match: data.matches?.[0] || null,
        });
      } else {
        setDuplicateState({ checking: false, email: cleaned, duplicate: false });
      }
    } catch (e) {
      setEmailState({ loading: false, email: cleaned, status: "error", sub_status: "request_failed", acceptable: false });
      setDuplicateState({ checking: false, email: cleaned, duplicate: false });
    }
  };

  // Debounced revalidation triggered onBlur of email input. Trusted-
  // upload leads skip the ZeroBounce round-trip entirely — the admin
  // chose to trust these rows at upload time.
  const handleEmailBlur = () => {
    if (isTrustedLead) return;
    if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);
    emailDebounceRef.current = setTimeout(() => validateEmailNow(form.work_email), 100);
  };

  const resetForm = () => {
    setForm({ ...EMPTY_LEAD, date: todayIsoDate() });
    setEmailState(null);
    setDuplicateState(null);
    setCampaignRef(null);
    setError("");
    if (formTopRef.current) formTopRef.current.scrollTop = 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isQA || isAssignee) {
      // Required-field check (only enforced for NEW leads; edits may patch sparse records)
      if (!isEdit) {
        const missing = REQUIRED_FIELD_NAMES.filter((n) => !(form[n] && String(form[n]).trim()));
        if (missing.length > 0) {
          setError(`Please fill in all required fields. Missing: ${missing.length} field${missing.length === 1 ? "" : "s"}.`);
          // Scroll to the first missing field
          try {
            const el = document.querySelector(`[data-testid="field-${missing[0]}"]`);
            if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
            if (el && el.focus) el.focus();
          } catch (err) {
            // scrollIntoView/focus can throw in non-DOM contexts (e.g., SSR). Non-fatal.
            console.warn("[LeadForm] scroll-to-missing-field failed", err);
          }
          return;
        }
      }
      if (!isEdit && !form.work_email) {
        setError("Work Email is required.");
        return;
      }
      // Strict client-side format guard — block submit BEFORE we burn a
      // ZeroBounce credit on an obviously typo'd email (spaces, missing
      // @, missing TLD). Skipped for trusted-upload leads so agents can
      // resubmit rows whose emails were intentionally accepted as-is.
      if (!isTrustedLead && form.work_email && !isEmailFormatValid(form.work_email)) {
        setError("Invalid email format. Please enter a valid email like name@example.com (no spaces).");
        try {
          const el = document.querySelector('[data-testid="field-work_email"]');
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
          if (el && el.focus) el.focus();
        } catch { /* non-DOM context; safe to skip */ }
        return;
      }
      // Phone validation intentionally LENIENT per admin spec — the
      // PhoneInputField surfaces a per-country example placeholder so
      // agents can visualize the expected shape, but submission is
      // never blocked on phone format. The country flag dropdown +
      // dynamic example are guidance only.
      // Strict website format guard — must match www.<domain>.<tld>.
      // Mirrors the inline error shown next to the field. Also skipped
      // for trusted-upload leads (matches the email bypass above).
      if (!isTrustedLead && form.website && !isWebsiteFormatValid(form.website)) {
        setError("Invalid website format. Please enter the website like www.xyz.com (no http://, no spaces, no paths).");
        try {
          const el = document.querySelector('[data-testid="field-website"]');
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
          if (el && el.focus) el.focus();
        } catch { /* non-DOM context */ }
        return;
      }
      if (!isTrustedLead && form.work_email && (!emailState || emailState.loading || emailState.email !== form.work_email.toLowerCase().trim())) {
        // Validate inline before submit
        await validateEmailNow(form.work_email);
      }
      const last = emailState;
      if (!isTrustedLead && last && last.loading) {
        setError("Email validation in progress, please wait.");
        return;
      }
      // Inline survey questions — when campaign_type is in
      // MANDATORY_CT, every question must have an answer. Mirrors the
      // server-side guard so the user gets immediate feedback.
      if (!isEdit && allQuestionsRequired && inlineQuestions.length > 0) {
        const ans = form.survey_answers || {};
        const missing = inlineQuestions.filter((q) => {
          const v = ans[q.id];
          if (v === undefined || v === null) return true;
          if (typeof v === "string" && !v.trim()) return true;
          if (Array.isArray(v) && v.length === 0) return true;
          return false;
        });
        if (missing.length > 0) {
          setError(
            `Campaign type "${form.campaign_type}" requires every survey question to be answered. ` +
            `Missing: ${missing.slice(0, 5).map((q) => q.prompt).join(", ")}` +
            (missing.length > 5 ? " …" : "")
          );
          try {
            const el = document.querySelector('[data-testid="lead-form-survey-section"]');
            if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
          } catch { /* non-DOM */ }
          return;
        }
      }
    }
    setLoading(true);
    try {
      const payload = { ...form };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      if (isEdit) {
        await api.put(`/leads/${id}`, payload);
        toast.success("Lead updated");
        navigate(role === "agent" ? "/my-leads" : "/leads");
      } else {
        await api.post("/leads", payload);
        toast.success("Lead saved · added to Master + Campaign exclusion lists", { duration: 5000 });
        window.dispatchEvent(new Event("lead-submitted"));
        resetForm();
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead deleted");
      navigate("/leads");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Disable submit if a known-bad email is present OR a duplicate is detected
  const submitDisabled = useMemo(() => {
    if (loading) return true;
    if (isQA) return false;
    // Trusted-upload leads bypass every format/acceptability gate so
    // agents can save without hitting the "Invalid email" wall on rows
    // the admin explicitly accepted as-is at bulk-upload time.
    if (isTrustedLead) return false;
    const emailLower = (form.work_email || "").toLowerCase().trim();
    if (emailState && !emailState.loading && emailState.email === emailLower) {
      if (!emailState.acceptable) return true;
    }
    if (duplicateState && !duplicateState.checking && duplicateState.email === emailLower) {
      if (duplicateState.duplicate) return true;
    }
    // Phone format is intentionally NOT used to disable submit — see
    // the matching note in onSubmit. The country-aware placeholder in
    // PhoneInputField is visual guidance only.
    // Block submit when website is present but does not match the
    // strict www.<domain>.<tld> format per admin spec.
    if (form.website && !isWebsiteFormatValid(form.website)) return true;
    return false;
  }, [loading, isQA, isTrustedLead, emailState, duplicateState, form.work_email, form.work_phone, form.website]);

  return (
    <div className="flex flex-col h-screen" data-testid="lead-form-shell">
      {/* Sticky header with logos */}
      <header className="border-b border-border bg-background sticky top-0 z-20" data-testid="lead-form-header">
        <div className="px-6 lg:px-10 py-3 flex items-center justify-between gap-4">
          <img src={SBT_LOGO} alt="SBT Research" className="h-10 w-auto object-contain" data-testid="form-sbt-logo" />
          <div className="text-center">
            <div className="meta-label">{isEdit ? "Edit Record" : "New Lead"}</div>
            <h1 className="font-display text-xl lg:text-2xl font-bold tracking-tight">
              {isEdit ? "Edit Lead" : "Add Lead"}
            </h1>
          </div>
          <img src={MARTECH_LOGO} alt="Martech Metrix" className="h-10 w-auto object-contain" data-testid="form-martech-logo" />
        </div>
        <div className="px-6 lg:px-10 pb-3 flex items-center gap-3 flex-wrap">
          {role === "agent" && isEdit && (
            <Link to="/my-leads">
              <Button variant="outline" className="rounded-sm h-9" data-testid="back-to-my-leads">
                <ArrowLeft size={14} className="mr-1" /> Back to My Leads
              </Button>
            </Link>
          )}
          {role !== "agent" && (
            <Link to={isAssignee ? "/my-leads" : "/leads"}>
              <Button variant="outline" className="rounded-sm h-9" data-testid="back-to-leads">
                <ArrowLeft size={14} className="mr-1" /> Back to {isAssignee ? "My Leads" : "Leads"}
              </Button>
            </Link>
          )}
          {isAssignee && isEdit && (
            <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm badge-info" data-testid="assignee-mode-badge">
              Assignee mode · Full field access
            </span>
          )}
        </div>
      </header>

      {/* Scrollable body */}
      <form onSubmit={onSubmit} className="flex-1 min-h-0 flex flex-col" data-testid="lead-form">
        <div ref={formTopRef} className="flex-1 min-h-0 overflow-y-auto">
          <div className="flex flex-col md:flex-row gap-6 md:gap-10 p-6 lg:p-10">
            {/* Form column — single column, fields stacked top→bottom */}
            <div className="flex-1 min-w-0 space-y-10 w-full max-w-[760px]">
              {/* QA notice — only show if backend's effective permissions
                  actually limit edits for this user (i.e., there's at least
                  one non-Audit/QA field marked view/hidden). Removes the
                  misleading hardcoded banner that ignored admin overrides
                  (2026-07-01 fix). */}
              {isQA && !isAssignee && (() => {
                if (!effectivePerms) return false;
                // Banner only appears when AT LEAST one non-QA field is
                // still locked. If admin has fully unlocked the QA role,
                // the banner is hidden.
                return Object.entries(effectivePerms).some(
                  ([k, v]) => !QA_EDITABLE.has(k) && v !== "edit"
                );
              })() && (
                <div className="badge-warning px-4 py-3 rounded-sm text-sm" data-testid="qa-readonly-notice">
                  Some fields are read-only based on your role&apos;s permissions. Contact an admin to request edit access.
                </div>
              )}
              {!isQA && <QuickExclusionPanel defaultOpen={!isEdit} />}
              {/* Fallback: the Reassigned Agent Name row normally renders
                  under the Agent Name field — but if this role's perms mark
                  agent_name as hidden, surface it standalone so QA/admin can
                  always see who resubmitted a reassigned lead. */}
              {isEdit && reassignedAgentDisplay && effectivePerms && (effectivePerms["agent_name"] || "edit") === "hidden" && (
                <div data-testid="reassigned-agent-standalone">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Reassigned Agent Name
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-sm badge-info">read-only</span>
                  </Label>
                  <div className="mt-1">
                    <input
                      type="text"
                      value={reassignedAgentDisplay}
                      readOnly
                      data-testid="reassigned-agent-standalone-input"
                      className="h-9 rounded-sm font-body w-full text-sm border border-input bg-muted/40 px-3 cursor-not-allowed"
                    />
                  </div>
                  {form.resubmitted_by_id && form.resubmission_date && (
                    <div className="mt-1 text-[10px] text-muted-foreground font-mono">
                      Resubmitted on {form.resubmission_date}
                    </div>
                  )}
                </div>
              )}
              {LEAD_SECTIONS.map((section) => {
                const visibleFields = section.fields.filter((f) => (effectivePerms ? (effectivePerms[f.name] || "edit") : "edit") !== "hidden");
                if (visibleFields.length === 0) return null;
                return (
                <section key={section.title}>
                  <div className="flex items-baseline gap-3 mb-4">
                    <h2 className="font-display text-xl font-bold">{section.title}</h2>
                    <div className="flex-1 border-t border-border" />
                    {section.optional && (
                      <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded-sm badge-neutral" data-testid={`section-optional-${section.title}`}>
                        Optional
                      </span>
                    )}
                    <span className="meta-label">{visibleFields.length} field{visibleFields.length === 1 ? "" : "s"}</span>
                  </div>
                  <div className="grid grid-cols-1 gap-y-5">
                    {section.fields.map((field) => {
                      // Field-level permissions (from custom roles): hidden | view | edit
                      // Assignment override: if user is an assignee on this lead, treat as all-edit (admin view).
                      const perm = effectivePerms ? (effectivePerms[field.name] || "edit") : "edit";
                      if (perm === "hidden") return null;
                      const isViewOnly = perm === "view";
                      // Date column on the Add Lead form: only admin / team_lead
                      // / qa may override the auto-stamped "today" value.
                      // Agents see a locked input — backend will hard-stamp
                      // today's date on submit regardless.
                      const isLockedDateForAgent = field.name === "date" && !DATE_OVERRIDE_ROLES.has(role);
                      // 2026-07-01 fix: REMOVED the hardcoded
                      // `isQA && !isAssignee && !QA_EDITABLE.has(...)` clause.
                      // The form now trusts `effectivePerms` from the backend
                      // (which honours admin's saved overrides on the QA
                      // custom-role doc). The QA_EDITABLE set remains only
                      // for the assignee carve-out / submit-validation logic
                      // (below). Date lock for agents stays — agents must
                      // never override "today" regardless of perms.
                      const disabled = isViewOnly || isLockedDateForAgent;
                      const isEmail = field.name === "work_email";
                      const isRequired = !isQA && !isViewOnly && !section.optional && !field.optional;
                      const showDup = isEmail && !isQA && !isViewOnly && duplicateState && !duplicateState.checking
                        && duplicateState.email === (form.work_email || "").toLowerCase().trim()
                        && duplicateState.duplicate;
                      return (
                        <div key={field.name}>
                          <div className="flex items-center justify-between gap-2">
                            <Label htmlFor={`field-${field.name}`} className="text-xs uppercase tracking-wider text-muted-foreground">
                              {field.label}
                              {isRequired && <span className="text-red-600 ml-1" aria-label="required">*</span>}
                              {isViewOnly && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-sm badge-info">read-only</span>}
                            </Label>
                            {isEmail && !isQA && !isViewOnly && <EmailValidationBadge state={emailState} />}
                            {isEmail && isTrustedLead && (
                              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm badge-info" data-testid="trusted-upload-badge">
                                Trusted upload · validation bypassed
                              </span>
                            )}
                          </div>
                          <div className="mt-1">
                            <FieldControl
                              field={field} value={form[field.name]}
                              onChange={onChange} optionMap={options || {}}
                              disabled={disabled}
                              onBlur={isEmail && !isViewOnly ? handleEmailBlur : undefined}
                              countryName={form.country}
                            />
                          </div>
                          {isEmail && !isQA && duplicateState?.checking && duplicateState.email === (form.work_email || "").toLowerCase().trim() && (
                            <div className="mt-1 text-[11px] text-muted-foreground inline-flex items-center gap-1" data-testid="duplicate-checking">
                              <Loader2 size={11} className="animate-spin" /> Checking master exclusion list…
                            </div>
                          )}
                          {/* Strict client-side format check — shows before
                              ZeroBounce kicks in so a typo'd email never
                              gets pushed up the validation chain. Skipped
                              for trusted-upload leads (backend mirrors). */}
                          {isEmail && !isViewOnly && !isTrustedLead && form.work_email && !isEmailFormatValid(form.work_email) && (
                            <div className="mt-1 text-[11px] text-red-600 flex items-center gap-1" data-testid="email-format-error">
                              <AlertTriangle size={11} /> Invalid email format. Please enter a valid email like name@example.com (no spaces).
                            </div>
                          )}
                          {/* Strict website format check — admin requires
                              the canonical `www.xyz.com` shape. Skipped
                              for trusted-upload leads (matches email). */}
                          {field.name === "website" && !isViewOnly && !isTrustedLead && form.website && !isWebsiteFormatValid(form.website) && (
                            <div className="mt-1 text-[11px] text-red-600 flex items-center gap-1" data-testid="website-format-error">
                              <AlertTriangle size={11} /> Invalid website format. Example: www.xyz.com (no http://, no spaces, no paths).
                            </div>
                          )}
                          {showDup && (
                            <div className="mt-2 badge-error px-3 py-2 rounded-sm text-xs flex items-start gap-2" data-testid="duplicate-warning">
                              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                              <div>
                                <div className="font-semibold">Duplicate detected — this email already exists.</div>
                                <div className="font-mono text-[10px] mt-0.5">
                                  Match: {duplicateState.first_match?.work_email} · Campaign: {duplicateState.first_match?.campaign_name || "—"} · Lead ID: {duplicateState.first_match?.lead_id || duplicateState.first_match?.id?.slice(0,8)}
                                </div>
                                <div className="mt-1">Submission is blocked. Change the email or remove the existing record.</div>
                              </div>
                            </div>
                          )}
                          {field.name === "agent_name" && reassignedAgentDisplay && (
                            <div className="mt-4" data-testid="reassigned-agent-row">
                              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                                Reassigned Agent Name
                                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-sm badge-info">read-only</span>
                              </Label>
                              <div className="mt-1">
                                <input
                                  type="text"
                                  value={reassignedAgentDisplay}
                                  readOnly
                                  data-testid="reassigned-agent-input"
                                  className="h-9 rounded-sm font-body w-full text-sm border border-input bg-muted/40 px-3 cursor-not-allowed"
                                />
                              </div>
                              {form.resubmitted_by_id && form.resubmission_date && (
                                <div className="mt-1 text-[10px] text-muted-foreground font-mono" data-testid="resubmitted-meta">
                                  Resubmitted on {form.resubmission_date}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
                );
              })}

              {/* ── Inline survey questions (admin-defined per campaign) ── */}
              {inlineQuestions.length > 0 && (
                <section data-testid="lead-form-survey-section">
                  <div className="flex items-baseline gap-3 mb-4">
                    <h2 className="font-display text-xl font-bold">Campaign Questions</h2>
                    <div className="flex-1 border-t border-border" />
                    {allQuestionsRequired && (
                      <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded-sm badge-error">
                        Required for {form.campaign_type}
                      </span>
                    )}
                    <span className="meta-label">{inlineQuestions.length} question{inlineQuestions.length === 1 ? "" : "s"}</span>
                  </div>
                  <div className="grid grid-cols-1 gap-y-5">
                    {inlineQuestions.map((q) => (
                      <SurveyQuestionField
                        key={q.id}
                        question={q}
                        value={form.survey_answers?.[q.id]}
                        onChange={(v) => onChange("survey_answers", { ...(form.survey_answers || {}), [q.id]: v })}
                        required={allQuestionsRequired || q.required}
                        readOnly={isEdit && !canFillSurveyOnEdit}
                      />
                    ))}
                  </div>
                </section>
              )}

              {error && <div data-testid="lead-form-error" className="badge-error px-4 py-3 rounded-sm text-sm">{error}</div>}
            </div>

            {/* Campaign reference column — sticky to the right of the form */}
            <aside className="md:w-[340px] lg:w-[380px] xl:w-[420px] md:shrink-0">
              <div className="md:sticky md:top-4">
                <CampaignReferencePanel campaign={campaignRef} />
              </div>
            </aside>
          </div>
        </div>

        {/* Sticky footer */}
        <footer className="border-t border-border bg-background sticky bottom-0 z-20" data-testid="lead-form-footer">
          <div className="px-6 lg:px-10 py-4 flex items-center gap-2 flex-wrap">
            <Button
              type="submit"
              disabled={submitDisabled}
              className="rounded-sm bg-primary hover:bg-[#00227A] h-10 px-8"
              data-testid="lead-form-submit"
            >
              {loading ? "Saving…" : isEdit ? "Save Changes" : "Submit & Reset"}
            </Button>
            {!isEdit && (
              <Button
                type="button" variant="outline" className="rounded-sm h-10"
                onClick={resetForm} data-testid="lead-form-reset"
              >
                Reset Form
              </Button>
            )}
            {role !== "agent" && (
              <Link to="/leads">
                <Button type="button" variant="outline" className="rounded-sm h-10" data-testid="lead-form-cancel">
                  Cancel
                </Button>
              </Link>
            )}
            {isEdit && isAdmin && (
              <Button
                type="button" variant="outline"
                className="rounded-sm h-10 text-red-600 hover:text-red-700 ml-auto"
                onClick={onDelete} data-testid="delete-lead-button"
              >
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
            )}
            {emailState && !emailState.loading && !emailState.acceptable && !isQA && (
              <span className="text-xs badge-error px-2 py-1 rounded-sm ml-auto" data-testid="email-block-reason">
                Email rejected by ZeroBounce — fix the work email to submit.
              </span>
            )}
            {duplicateState && !duplicateState.checking && duplicateState.duplicate && !isQA && (
              <span className="text-xs badge-error px-2 py-1 rounded-sm ml-auto" data-testid="duplicate-block-reason">
                Duplicate email — already in the exclusion list.
              </span>
            )}
          </div>
        </footer>
      </form>
    </div>
  );
}
